import { create } from 'zustand';
import type { FacetValue } from '../utils/device-facets';

/**
 * Status filter values for the MDM devices page.
 * - `all` — no client-side filter applied.
 * - `online` — device heartbeat is fresh.
 * - `offline` — heartbeat missing or stale.
 * - `stale` — device has not checked in for an extended window (but not fully offline).
 */
export type MdmDeviceStatusFilter = 'all' | 'online' | 'offline' | 'stale';

/**
 * Fleet filter:
 * - `undefined` — "All fleets" (no fleet filter).
 * - `null` — "Default" (devices with fleetId=null).
 * - `string` — specific fleetId.
 */
export type MdmDeviceFleetFilter = string | null | undefined;

/**
 * Team and folder are SEPARATE, MULTI-VALUE filters on purpose — a screen's
 * owning team and the folder it sits in are independent, and an account can
 * organise entirely by folder while every team owns zero. Each holds a set of
 * picked values (empty = no filter); `null` inside the set is the "not set"
 * bucket. See `utils/device-facets.ts`.
 */
export type MdmDeviceFacetFilter = FacetValue[];

interface MdmDevicesFilterState {
  searchText: string;
  fleetId: MdmDeviceFleetFilter;
  teamIds: MdmDeviceFacetFilter;
  folderPaths: MdmDeviceFacetFilter;
  status: MdmDeviceStatusFilter;
  os: string[];
  tags: string[];

  setSearchText: (text: string) => void;
  setFleetId: (fleetId: MdmDeviceFleetFilter) => void;
  setTeamIds: (teamIds: MdmDeviceFacetFilter) => void;
  setFolderPaths: (folderPaths: MdmDeviceFacetFilter) => void;
  setStatus: (status: MdmDeviceStatusFilter) => void;
  setOs: (os: string[]) => void;
  setTags: (tags: string[]) => void;
  clear: () => void;
  hasActive: () => boolean;
}

export const useMdmDevicesFilterStore = create<MdmDevicesFilterState>((set, get) => ({
  searchText: '',
  fleetId: undefined,
  teamIds: [],
  folderPaths: [],
  status: 'all',
  os: [],
  tags: [],

  setSearchText: (searchText) => set({ searchText }),
  setFleetId: (fleetId) => set({ fleetId }),
  setTeamIds: (teamIds) => set({ teamIds }),
  setFolderPaths: (folderPaths) => set({ folderPaths }),
  setStatus: (status) => set({ status }),
  setOs: (os) => set({ os }),
  setTags: (tags) => set({ tags }),
  clear: () =>
    set({
      searchText: '',
      fleetId: undefined,
      teamIds: [],
      folderPaths: [],
      status: 'all',
      os: [],
      tags: [],
    }),
  hasActive: () => {
    const s = get();
    return (
      s.searchText.length > 0 ||
      s.fleetId !== undefined ||
      s.teamIds.length > 0 ||
      s.folderPaths.length > 0 ||
      s.status !== 'all' ||
      s.os.length > 0 ||
      s.tags.length > 0
    );
  },
}));
